import { Component } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Container, Typography } from '@mui/material';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import i18n from 'src/i18n/i18n';

const CHUNK_RELOAD_KEY = 'chunkReloadAt';

const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '');

// Filet de sécurité global : sans lui, une erreur inattendue dans n'importe quel
// composant fait disparaître toute l'interface (écran blanc) au lieu d'afficher
// juste ce message de remplacement.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, offline: false };
    this.handleReload = this.handleReload.bind(this);
    this.handleOnline = this.handleOnline.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Sans ce log, la vraie cause de l'erreur restait invisible (même dans la console).
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);

    // Fichier JS d'une page introuvable : arrive quand un onglet ouvert avant une
    // mise en ligne demande les anciens fichiers, qui n'existent plus. Recharger
    // récupère la nouvelle version. Une seule fois par 10 s, pour ne pas boucler
    // si le problème vient d'ailleurs (réseau coupé, serveur en panne...).
    // Hors ligne, recharger afficherait la page d'erreur de Chrome (dinosaure) :
    // on affiche un message et on recharge tout seul quand internet revient.
    if (isChunkLoadError(error) && navigator.onLine === false) {
      this.setState({ offline: true });
      window.addEventListener('online', this.handleOnline);
      return;
    }

    if (isChunkLoadError(error)) {
      try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
          window.location.reload();
        }
      } catch {
        // sessionStorage indisponible : on laisse l'écran d'erreur et son bouton
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
  }

  handleOnline() {
    window.location.reload();
  }

  handleReload() {
    window.location.reload();
  }

  render() {
    const { hasError, offline } = this.state;
    const { children } = this.props;

    if (hasError) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          textAlign="center"
          px={2}
          sx={{ bgcolor: '#0b0e13', color: '#ffffff' }}
        >
          <Container maxWidth="sm">
            <Typography variant="h2" sx={{ mb: 2, color: 'inherit' }}>
              {i18n.t(offline ? 'serverErrors.offlineTitle' : 'serverErrors.crashTitle')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: '#ffffff' }}>
              {i18n.t(offline ? 'serverErrors.offlinePage' : 'serverErrors.crashPage')}
            </Typography>
            {/* Hors ligne, recharger mènerait à la page d'erreur de Chrome :
                pas de bouton, la page se recharge seule au retour d'internet. */}
            {!offline && (
              <Button
                variant="contained"
                startIcon={<RefreshTwoToneIcon />}
                onClick={this.handleReload}
              >
                {i18n.t('serverErrors.reload')}
              </Button>
            )}
          </Container>
        </Box>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

export default ErrorBoundary;
